import { useState } from 'react'

import { Box, Button, Tag, Text } from '@factorialco/deprecated-design-system'
import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import TimePicker from 'modules/core/components/Inputs/TimePicker'
import MultiInput from 'modules/core/components/MultiInput'
import Popover from 'modules/core/components/PopoverMenu'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import AddPlannedBreakModal from 'modules/shift_management/components/Settings/SavedShifts/AddPlannedBreakModal'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'

interface Props {
  form: PlannedBreakForm
  error: string
  onDelete: (form: PlannedBreakForm) => void
  onAddBreak: (breakConfiguration: BreakConfigurationFragment, breakType: BreakType) => void
}
interface TypeProps {
  timePickerLabel?: string
  form: PlannedBreakForm
  error: string
  hideError?: boolean
}

const FlexibleTypeComponent = observer(({ form, error, hideError }: TypeProps) => {
  return (
    <MultiInput>
      <Box gap="s4">
        <Box flexDirection="row" alignItems="center" gap="s16">
          <Box width="fit-content">
            <TextField
              type="number"
              value={form.get('duration').value.toString()}
              onChange={(duration) => form.get('duration').set(duration)}
              placeholder="--"
              isNotValid={!!error}
              label={i18n.t('company_settings.time.breaks.duration')}
              hideLabel
            />
          </Box>
          <Box
            background="grey200"
            padding="s8"
            borderRadius={{
              all: 'abs008',
            }}
            border={{
              all: {
                width: 's2',
                style: 'solid',
                color: 'grey400',
              },
            }}
          >
            <Text.Normal>{i18n.t('common.minutes')}</Text.Normal>
          </Box>
          <Text size="200" weight="medium">
            {i18n.t('company_settings.time.breaks.duration').toLowerCase()}
          </Text>
        </Box>
        {error && !hideError && (
          <Text size="200" color="radicalRed1000" capitalize>
            {error}
          </Text>
        )}
      </Box>
    </MultiInput>
  )
})

const FixedTypeComponent = observer(
  ({
    timePickerLabel = i18n.t('company_settings.time.breaks.fixed_time').toLowerCase(),
    form,
    error,
  }: TypeProps) => {
    const { isTimeFormat24H } = useIsTimeFormat24H()
    return (
      <Box gap="s4">
        <Box flexDirection="row" alignItems="center" gap="s16">
          <Box flexDirection="row" justifyContent="flexStart" alignItems="center">
            <TimePicker
              field={form.get('start_at')}
              isTimeFormat24H={isTimeFormat24H}
              isNotValid={!!error}
              mandatory
            />
            <Box paddingX="s8">-</Box>
            <TimePicker
              field={form.get('end_at')}
              isTimeFormat24H={isTimeFormat24H}
              isNotValid={!!error}
              mandatory
            />
          </Box>

          <Text size="200" weight="medium">
            {timePickerLabel}
          </Text>
        </Box>
        {error && (
          <Box alignItems="flexStart">
            <Text size="200" color="radicalRed1000" capitalize>
              {error}
            </Text>
          </Box>
        )}
      </Box>
    )
  }
)

const SemiFlexibleTypeComponent = observer(({ form, error }: TypeProps) => {
  return (
    <Box gap="s8">
      {/* Hide error msg here to avoid duplicated messages */}
      <FlexibleTypeComponent form={form} error={error} hideError />
      <FixedTypeComponent
        form={form}
        error={error}
        timePickerLabel={i18n.t('company_settings.time.breaks.time_frame').toLowerCase()}
      />
    </Box>
  )
})

const Item = ({ form, onDelete, onAddBreak, error }: Props) => {
  const [openAddBreakModal, setOpenAddBreakModal] = useState<boolean>(false)

  const onOpenAddBreakModal = () => {
    setOpenAddBreakModal(true)
  }
  return (
    <>
      <Box flexDirection="column" gap="s16">
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Box flexDirection="row" gap="s8">
            <Text.Normal size="300" weight="medium" color="grey800">
              {form.get('break_configuration.name').value}
            </Text.Normal>
            <Box width="fit-content">
              <Tag
                type={
                  form.get('break_configuration.paid').value ? Tag.Types.lime : Tag.Types.malibu
                }
              >
                {form.get('break_configuration.paid').value
                  ? i18n.t('company_settings.time.breaks.paid')
                  : i18n.t('company_settings.time.breaks.unpaid')}
              </Tag>
            </Box>
          </Box>
          <Box flexDirection="row" gap="s8">
            <Button type="secondary" onClick={() => onDelete(form)} iconName="delete" />
            <Popover type="list">
              <Popover.Trigger>
                <Button type="secondary" iconName="add" />
              </Popover.Trigger>
              <Popover.Content side="bottom" align="end" width="s200">
                <Popover.TouchableOption
                  onClick={() => onOpenAddBreakModal()}
                  title={i18n.t('shift_management.saved_shifts.actions.add.break')}
                />
              </Popover.Content>
            </Popover>
          </Box>
        </Box>

        {/* Use showNotice once the refactor is done with the new layout of breaks */}
        {form.isFlexible && <FlexibleTypeComponent form={form} error={error} />}
        {form.isFixed && <FixedTypeComponent form={form} error={error} />}
        {form.isSemiFlexible && <SemiFlexibleTypeComponent form={form} error={error} />}
      </Box>
      {openAddBreakModal && (
        <AddPlannedBreakModal
          onAddBreak={onAddBreak}
          onClose={() => setOpenAddBreakModal(false)}
          from="saved-shifts"
        />
      )}
    </>
  )
}
export default observer(Item)
